import { useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import clsx from 'clsx';

import type { OrganizationsResponse, OrgRow, OverviewResponse } from '../lib/types';
import {
  CUSTOMER_FILTERS,
  JUSTIFI_BUCKETS,
  TELNYX_BUCKETS,
  bucketCounts,
  organizationRow,
  filterRows,
  isCurrent,
  isListed,
  lastCompleteMonth,
  matchesSearch,
  parseFilter,
  sortRows,
  statusSubline,
  type BucketDef,
  type CustomerFilter,
  type OrganizationRow,
} from '../lib/organizations';
import { formatCount, formatCurrency, formatMonth, formatPercent } from '../lib/format';
import { ErrorState, LoadingState, MonthTextPicker, PageHeader, TableScroll, useInsightsQuery } from '../components/ui';
import { CustomerActionsMenu, HealthPill, Pill, vendorTone } from '../components/organization';

/** The window both queries load — the same key Home uses, so its figures are shared, not refetched. */
const WINDOW_MONTHS = 6;

function StatusBar<B extends string>({
  title,
  note,
  counts,
}: {
  title: string;
  note: string;
  counts: Array<BucketDef<B> & { count: number }>;
}) {
  const total = counts.reduce((sum, c) => sum + c.count, 0);
  return (
    <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-card sm:p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-bold text-stone-900">{title}</h2>
        <span className="text-xs text-stone-500">{formatCount(total)} companies</span>
      </div>
      <div className="mt-3.5 flex h-3 gap-0.5 overflow-hidden rounded-full bg-stone-100" aria-hidden>
        {counts
          .filter((c) => c.count > 0)
          .map((c) => (
            <div key={c.bucket} className={c.tone} style={{ width: `${(c.count / Math.max(total, 1)) * 100}%` }} />
          ))}
      </div>
      <ul className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 text-xs text-stone-700 sm:grid-cols-2 xl:grid-cols-3">
        {counts.map((c) => (
          <li key={c.bucket} className="flex items-center gap-1.5">
            <span className={clsx('inline-block h-2.5 w-2.5 shrink-0 rounded-sm', c.tone)} aria-hidden />
            <b className="tabular font-semibold text-stone-900">{formatCount(c.count)}</b>
            <span>{c.label}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] leading-relaxed text-stone-400">{note}</p>
    </section>
  );
}

function StatusCell({ status, now }: { status: OrgRow['status']['justifi'] | OrgRow['status']['telnyx']; now: Date }) {
  const sub = statusSubline(status, now);
  return (
    <div title={status.detail ?? undefined}>
      <Pill tone={vendorTone(status.bucket, status.waitingOn)}>{status.label}</Pill>
      {sub && <div className="mt-1 text-xs text-stone-500">{sub}</div>}
    </div>
  );
}

function MoneyCell({ value, loading }: { value: string; loading: boolean }) {
  return <span className={clsx('tabular', loading && 'text-stone-300')}>{loading ? '…' : value}</span>;
}

function CustomerTableRow({
  row,
  now,
  moneyLoading,
}: {
  row: OrganizationRow;
  now: Date;
  moneyLoading: boolean;
}) {
  const { org } = row;
  const lost = org.status.health.level === 'lost';
  return (
    <tr className={clsx('border-b border-stone-100 align-top', lost && 'bg-stone-50/60')}>
      <td className="py-3 pr-3">
        <Link
          to={`/organizations/${encodeURIComponent(org.orgId)}`}
          className="font-medium text-stone-900 hover:text-primary hover:underline"
        >
          {org.name}
        </Link>
        <div className="mt-0.5 text-xs text-stone-500">since {formatMonth(org.createdAt.slice(0, 7))}</div>
      </td>
      <td className="py-3 pr-3">
        <StatusCell status={org.status.justifi} now={now} />
      </td>
      <td className="py-3 pr-3">
        <StatusCell status={org.status.telnyx} now={now} />
      </td>
      <td className="py-3 pr-3">
        {row.cardRevenue > 0 ? (
          <>
            <div className="tabular font-medium text-stone-900">{formatCurrency(row.cardRevenue)}</div>
            <div className="text-xs text-stone-500">of {formatCurrency(row.collected)} collected</div>
          </>
        ) : (
          <div className="text-xs text-stone-500">
            {org.status.justifi.bucket === 'live'
              ? 'None this month'
              : org.status.justifi.bucket === 'approved'
                ? 'None yet'
                : 'None — no Ruta Pay'}
          </div>
        )}
      </td>
      <td className="py-3 pr-3 text-right">
        {row.fee === null ? (
          <span className="text-stone-400">—</span>
        ) : (
          <span className="tabular" title={row.feeWithRutaPay === null ? 'What we last charged' : row.feeWithRutaPay ? 'With Ruta Pay' : 'Without Ruta Pay'}>
            {formatCurrency(row.fee)}
          </span>
        )}
      </td>
      <td className="py-3 pr-3 text-right font-medium text-stone-900">
        <MoneyCell value={formatCurrency(row.toRuta)} loading={moneyLoading} />
      </td>
      <td className="py-3 pr-3 text-right text-stone-700" title={row.countedOnHome === false ? 'Not in Home’s totals this month' : undefined}>
        <MoneyCell value={formatCurrency(row.subscriptionMargin)} loading={moneyLoading} />
        {!moneyLoading && row.marginPct !== null && (
          <div className="tabular text-xs text-stone-500">{formatPercent(row.marginPct, 0)}</div>
        )}
      </td>
      <td className="py-3 pr-2">
        <HealthPill level={org.status.health.level} />
        {org.status.health.reason && (
          <div className="mt-1 text-xs text-stone-500">{org.status.health.reason}</div>
        )}
      </td>
      <td className="py-2.5 text-right">
        <CustomerActionsMenu org={org} />
      </td>
    </tr>
  );
}

/**
 * Admin Insights organization list (RUT-4072): where every company stands with
 * JustiFi and Telnyx, what it brings in, and what to do next from its row.
 */
export function OrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const filter = parseFilter(searchParams.get('filter'));

  const orgsQuery = useInsightsQuery<OrganizationsResponse>(
    ['organizations', WINDOW_MONTHS],
    `/organizations?months=${WINDOW_MONTHS}`,
  );
  // To Ruta and margin are Home's own per-company figures.
  const moneyQuery = useInsightsQuery<OverviewResponse>(
    ['overview', WINDOW_MONTHS],
    `/costs/platform?months=${WINDOW_MONTHS}`,
  );

  const data = orgsQuery.data;
  const month =
    selectedMonth && data?.months.includes(selectedMonth)
      ? selectedMonth
      : data
        ? lastCompleteMonth(data.months, data.partialMonth)
        : '';

  const rows = useMemo(() => {
    if (!data) return [];
    return data.organizations.filter(isListed).map((org) => organizationRow(org, month, moneyQuery.data));
  }, [data, month, moneyQuery.data]);

  // The old list linked companies as ?orgId=…; send those to the account page.
  const legacyOrgId = searchParams.get('orgId');
  if (legacyOrgId) return <Navigate to={`/organizations/${encodeURIComponent(legacyOrgId)}`} replace />;

  if (orgsQuery.isLoading) return <LoadingState />;
  if (orgsQuery.error || !data) return <ErrorState error={orgsQuery.error} />;

  const now = new Date();
  const current = rows.filter((row) => isCurrent(row.org)).map((row) => row.org);
  const shown = sortRows(filterRows(rows, filter).filter((row) => matchesSearch(row.org, search)));
  const notCounted = data.organizations.filter((org) => !isListed(org));
  const monthLabel = formatMonth(month);

  const setFilter = (key: CustomerFilter) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === 'all') next.delete('filter');
        else next.set('filter', key);
        return next;
      },
      { replace: true },
    );

  return (
    <>
      <PageHeader
        title="Organizations"
        subtitle="Where every company stands with JustiFi and Telnyx, what it brings in, and who has to move next"
        actions={
          <MonthTextPicker months={data.months} value={month} onChange={setSelectedMonth} label="Money for" />
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <StatusBar
          title="JustiFi — Ruta Pay"
          note="Companies not lost. Live means JustiFi approved them and they have taken at least one card payment through Ruta Pay since signup."
          counts={bucketCounts(current, JUSTIFI_BUCKETS, (org) => org.status.justifi.bucket)}
        />
        <StatusBar
          title="Telnyx — own texting number"
          note="Nothing moves a registration forward on its own yet, and no company texts from its own number — every text still goes out on Ruta's shared number."
          counts={bucketCounts(current, TELNYX_BUCKETS, (org) => org.status.telnyx.bucket)}
        />
      </div>

      <section className="mb-6 rounded-xl border border-stone-200 bg-white p-4 shadow-card sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter organizations">
            {CUSTOMER_FILTERS.map((f) => {
              const count = filterRows(rows, f.key).length;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter(f.key)}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    active
                      ? 'border-primary/30 bg-primary/10 text-primary'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-900',
                  )}
                >
                  {f.label}
                  <span className="tabular text-stone-400">{formatCount(count)}</span>
                </button>
              );
            })}
          </div>
          <div className="relative lg:w-64">
            <MagnifyingGlass
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400"
              weight="bold"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Company, owner email or ID"
              aria-label="Search organizations"
              className="w-full rounded-md border border-stone-200 bg-stone-50 py-2 pl-8 pr-2.5 text-sm text-stone-800 outline-none placeholder:text-stone-400 focus:border-primary/40 focus:bg-white focus:ring-2 focus:ring-primary/10"
            />
          </div>
        </div>

        {moneyQuery.error && (
          <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
            What each company brings in could not load, so To Ruta and Margin are blank.{' '}
            {moneyQuery.error instanceof Error ? moneyQuery.error.message : ''}
          </div>
        )}

        <div className="mt-4">
          <TableScroll>
            <table className="w-full min-w-[1040px] table-fixed text-sm">
              <colgroup>
                <col className="w-[16%]" />
                <col className="w-[14%]" />
                <col className="w-[15%]" />
                <col className="w-[13%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[12%]" />
                <col className="w-[4%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                  <th className="py-2.5 pr-3 font-medium">Company</th>
                  <th className="py-2.5 pr-3 font-medium">JustiFi</th>
                  <th className="py-2.5 pr-3 font-medium">Telnyx</th>
                  <th className="py-2.5 pr-3 font-medium">Card revenue, {monthLabel}</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Fee</th>
                  <th className="py-2.5 pr-3 text-right font-medium">To Ruta</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Margin</th>
                  <th className="py-2.5 pr-2 font-medium">Health</th>
                  <th className="py-2.5">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-sm text-stone-400">
                      No companies match
                    </td>
                  </tr>
                )}
                {shown.map((row) => (
                  <CustomerTableRow key={row.org.orgId} row={row} now={now} moneyLoading={moneyQuery.isLoading} />
                ))}
              </tbody>
            </table>
          </TableScroll>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-stone-500">
          Showing {formatCount(shown.length)} of {formatCount(rows.length)}. Card revenue is what their customers paid
          by card through Ruta Pay. Fee is the plan fee on the newest paid invoice — what we actually charged, whatever month is picked; for one we have never invoiced, what the plan would charge ($150 with Ruta Pay, $375 without). To
          Ruta is what we invoiced them plus the Ruta Pay spread, for months Home counts them; Margin is subscription
          margin — it takes off only the costs they cause, not shared costs.
        </p>
        {notCounted.length > 0 && (
          <p className="mt-1.5 text-xs text-stone-400">
            Not in any total:{' '}
            {notCounted.map((org, index) => (
              <span key={org.orgId}>
                {index > 0 && ', '}
                <Link to={`/organizations/${encodeURIComponent(org.orgId)}`} className="hover:underline">
                  {org.name}
                </Link>{' '}
                ({org.status.health.reason?.toLowerCase()})
              </span>
            ))}
          </p>
        )}
      </section>
    </>
  );
}
